import { useState, useEffect, ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import LessonContext from './LessonIdCtx';
import { Lesson, Chapter } from '@/types/types';
import AXIOS from '@/util/axios';
import { useAuth } from '@/hooks/useAuth';

export const LessonProvider: React.FC<{
  children: ReactNode;
  loadLessonData?: boolean;
  initialLessonId?: number;
  courseId?: string;
}> = ({ children, loadLessonData = false, initialLessonId, courseId }) => {
  const [currentLessonId, setCurrentLessonId] = useState<number>(
    initialLessonId ?? 1
  );
  const [lessonType, setLessonType] = useState<string | null>(null);

  useEffect(() => {
    if (
      typeof initialLessonId === 'number' &&
      Number.isFinite(initialLessonId) &&
      initialLessonId > 0 &&
      initialLessonId !== currentLessonId
    ) {
      setCurrentLessonId(initialLessonId);
    }
  }, [currentLessonId, initialLessonId]);

  const { authState } = useAuth();

  const {
    data: lesson,
    error: lessonError,
    isLoading: lessonLoading,
    isFetching: lessonFetching,
    isPlaceholderData: lessonIsPlaceholder,
  } = useQuery<Lesson>({
    queryKey: ['lessonData', currentLessonId],
    queryFn: async () => {
      const { data } = await AXIOS.get(`/api/courses/lessons/${currentLessonId}`);
      return data;
    },
    enabled: loadLessonData && !!authState?.token && !!currentLessonId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });

  const {
    data: lessonTextHtml,
    error: lessonTextError,
    isLoading: lessonTextLoading,
    isFetching: lessonTextFetching,
    isPlaceholderData: lessonTextIsPlaceholder,
  } = useQuery<string>({
    queryKey: ['lessonTextHtml', currentLessonId, lessonType],
    queryFn: async () => {
      const { data } = await AXIOS.get(
        `/api/courses/lessons/${currentLessonId}/text`,
        { responseType: 'text' }
      );
      return data;
    },
    enabled: loadLessonData && !!authState?.token && !!currentLessonId && !!lessonType && (lessonType === 'TEXT' || lessonType === 'TEXT_VIDEO'),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });

  const {
    data: chaptersData,
    error: chaptersError,
    isLoading: chaptersLoading,
  } = useQuery<Chapter[]>({
    queryKey: ['chapters', courseId],
    queryFn: async () => {
      const { data } = await AXIOS.get(
        `/api/courses/chapters?sort[orderId]=asc&filters[course][type]=equal&filters[course][value]=${courseId}`
      );
      return data._embedded?.items ?? [];
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!courseId,
  });

  const chapters = chaptersData;

  useEffect(() => {
    if (chapters) {
      for (const chapter of chapters) {
        if (chapter.lessons) {
          const found = chapter.lessons.find(l => l.id === currentLessonId);
          if (found) {
            setLessonType(found.type);
            return;
          }
        }
      }
      setLessonType(null);
    }
  }, [chapters, currentLessonId]);

  return (
    <LessonContext.Provider
      value={{
        currentLessonId,
        setCurrentLessonId,
        lesson: loadLessonData ? lesson : undefined,
        lessonLoading: loadLessonData ? lessonLoading : false,
        lessonError: loadLessonData ? (lessonError as Error | null) ?? null : null,
        lessonFetching: loadLessonData ? lessonFetching : false,
        lessonIsPlaceholder: loadLessonData ? lessonIsPlaceholder : false,
        lessonTextHtml: loadLessonData ? lessonTextHtml : undefined,
        lessonTextLoading: loadLessonData ? lessonTextLoading : false,
        lessonTextError: loadLessonData
          ? (lessonTextError as Error | null) ?? null
          : null,
        lessonTextFetching: loadLessonData ? lessonTextFetching : false,
        lessonTextIsPlaceholder: loadLessonData ? lessonTextIsPlaceholder : false,
        chapters,
        chaptersLoading,
        chaptersError,
        lessonType,
        setLessonType,
      }}
    >
      {children}
    </LessonContext.Provider>
  );
};
